import { Injectable } from '@angular/core';

/**
 * Media query strings for standard breakpoint names.
 * Used by `BazisMediaQueryService.matchBreakpoint()`.
 */
export const SIZE_TO_MEDIA = {
    xs: '(min-width: 0px)',
    sm: '(min-width: 576.98px)',
    md: '(min-width: 768.98px)',
    lg: '(min-width: 992.98px)',
    xl: '(min-width: 1024.98px)',
    xxl: '(min-width: 1200px)',
};

/**
 * **BazisMediaQueryService**
 *
 * Small helper service for checking viewport size breakpoints using `window.matchMedia`.
 * Useful for conditional UI behavior without directly hardcoding media queries in components.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisMediaQueryService {
    // Check if the window matches the media query
    // at the breakpoint passed
    // e.g. matchBreakpoint('sm') => true if screen width exceeds 576px
    /**
     * Check if the current viewport matches the named breakpoint.
     *
     * When `breakpoint` is empty/undefined, returns `true`.
     * When `window.matchMedia` is not available, returns `false`.
     *
     * @param breakpoint - Breakpoint name key from `SIZE_TO_MEDIA` (example: `sm`, `md`, `lg`).
     * @returns True when viewport matches breakpoint media query, otherwise false.
     */
    public matchBreakpoint(breakpoint: string): boolean {
        if (breakpoint === undefined || breakpoint === '') {
            return true;
        }
        if (window.matchMedia) {
            const mediaQuery = SIZE_TO_MEDIA[breakpoint];
            return window.matchMedia(mediaQuery).matches;
        }
        return false;
    }

    /**
     * Check if viewport is in \([min, max)\) breakpoint interval.
     *
     * Returns true when `min` matches and `max` does not match.
     *
     * @param min - Minimum breakpoint name (inclusive).
     * @param max - Maximum breakpoint name (exclusive).
     * @returns True when viewport is in the requested interval.
     */
    public inIntervalBreakpoint(min: string, max: string): boolean {
        return this.matchBreakpoint(min) && !this.matchBreakpoint(max);
    }

    /**
     * Check if the current viewport matches a custom media query string.
     *
     * When `mediaQuery` is empty/undefined, returns `true`.
     *
     * @param mediaQuery - CSS media query string (example: `(max-width: 600px)`).
     * @returns True when viewport matches, otherwise false.
     */
    public matchCustomBreakpoint(mediaQuery: string): boolean {
        if (mediaQuery === undefined || mediaQuery === '') {
            return true;
        }
        if (window.matchMedia) {
            return window.matchMedia(mediaQuery).matches;
        }
        return false;
    }
}
